import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Loader2, Camera } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import api from "../lib/api";

/* ─────────────────────────────────────────────────────────────────────
   <BetaFeedbackButton />
   Floating "Beta · Send feedback" pill bottom-left of every page.
   Auto-tags submission with shop, current URL, viewport, user-agent.
   ───────────────────────────────────────────────────────────────────── */

const CATEGORIES = [
  { id: "bug",       label: "Bug",        emoji: "🐛", tone: "red" },
  { id: "confusion", label: "Confusing",  emoji: "🤔", tone: "yellow" },
  { id: "missing",   label: "Missing",    emoji: "💡", tone: "yellow" },
  { id: "praise",    label: "Praise",     emoji: "🎉", tone: "green" },
  { id: "other",     label: "Other",      emoji: "💬", tone: "neutral" },
];

const SEVERITIES = [
  { id: "low",    label: "Low" },
  { id: "medium", label: "Medium" },
  { id: "high",   label: "High" },
];

export default function BetaFeedbackButton() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("bug");
  const [severity, setSeverity] = useState("medium");
  const [message, setMessage] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [shop, setShop] = useState(null);
  const location = useLocation();

  useEffect(() => {
    api.get("/shopify/status").then(r => setShop(r.data?.shop)).catch(() => {});
  }, []);

  const reset = () => {
    setCategory("bug");
    setSeverity("medium");
    setMessage("");
  };

  const submit = async () => {
    if (message.trim().length < 4) {
      toast.error("Please describe the issue (min 4 characters)");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/beta-feedback/", {
        category,
        severity,
        message: message.trim(),
        page_url: window.location.pathname + window.location.search,
        shop_domain: shop || undefined,
        user_agent: navigator.userAgent.substring(0, 400),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        contact_email: contactEmail.trim() || undefined,
      });
      toast.success("Thanks! Feedback received — we'll triage within 24h.");
      reset();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit feedback. Try again or email support@aigrowthoperator.app");
    }
    setSubmitting(false);
  };

  // Hide on the admin page itself + on onboarding (not useful)
  if (location.pathname.startsWith("/admin/beta-feedback")) return null;
  if (location.pathname.startsWith("/onboarding")) return null;

  const cat = CATEGORIES.find(c => c.id === category);

  return (
    <>
      {/* Floating trigger pill */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 left-5 lg:left-[14rem] z-50 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-yellow-100 hover:bg-yellow-200 border border-yellow-300 text-yellow-900 text-xs font-bold shadow-md transition-all"
        data-testid="beta-feedback-trigger"
        aria-label="Send beta feedback"
      >
        <MessageCircle className="w-3.5 h-3.5" />
        Beta · Send feedback
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="beta-feedback-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-yellow-700" />
              Send beta feedback
            </DialogTitle>
            <p className="text-xs text-zinc-500 mt-1">
              We auto-attach your shop, current page, and browser so you don't have to. Thanks for helping us improve.
            </p>
          </DialogHeader>

          {/* Category */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Category</label>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map(c => (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  data-testid={`beta-feedback-cat-${c.id}`}
                  className={cn(
                    "inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors",
                    category === c.id
                      ? c.tone === "red"    ? "bg-red-100 border-red-300 text-red-900" :
                        c.tone === "yellow" ? "bg-yellow-100 border-yellow-300 text-yellow-900" :
                        c.tone === "green"  ? "bg-green-100 border-green-300 text-green-900" :
                                              "bg-zinc-100 border-zinc-300 text-zinc-900"
                      : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"
                  )}
                >
                  <span>{c.emoji}</span> {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Severity (hide for praise) */}
          {category !== "praise" && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Severity</label>
              <div className="flex gap-1.5">
                {SEVERITIES.map(s => (
                  <button
                    key={s.id}
                    onClick={() => setSeverity(s.id)}
                    data-testid={`beta-feedback-sev-${s.id}`}
                    className={cn(
                      "px-3 py-1 rounded-md text-xs font-medium border transition-colors",
                      severity === s.id
                        ? s.id === "high"   ? "bg-red-100 border-red-300 text-red-900" :
                          s.id === "medium" ? "bg-yellow-100 border-yellow-300 text-yellow-900" :
                                              "bg-green-100 border-green-300 text-green-900"
                        : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"
                    )}
                  >{s.label}</button>
                ))}
              </div>
            </div>
          )}

          {/* Message */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">What happened?</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              placeholder={
                category === "bug"       ? "What did you click? What did you expect? What happened instead?" :
                category === "confusion" ? "Which screen confused you? What were you trying to do?" :
                category === "missing"   ? "What feature would solve a real problem for you?" :
                category === "praise"    ? "What did you love? What worked well?" :
                                            "Tell us what's on your mind..."
              }
              className="border-zinc-200 focus:border-green-300 text-sm"
              data-testid="beta-feedback-message"
            />
          </div>

          {/* Contact (optional) */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Email (optional · so we can follow up)</label>
            <Input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="you@yourshop.com"
              className="border-zinc-200 focus:border-green-300 text-sm"
              data-testid="beta-feedback-email"
            />
          </div>

          {/* Auto-tagged context preview */}
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-2.5 text-[11px] text-zinc-600 space-y-0.5">
            <p><span className="text-zinc-400">Shop:</span> {shop || "(not connected)"}</p>
            <p><span className="text-zinc-400">Page:</span> <code className="text-zinc-700">{location.pathname}</code></p>
            <p><span className="text-zinc-400">Viewport:</span> {typeof window !== "undefined" ? `${window.innerWidth}×${window.innerHeight}` : "—"}</p>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={submitting}
              data-testid="beta-feedback-cancel"
            >Cancel</Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className={cn(
                "font-bold border",
                cat?.tone === "red"    ? "bg-red-200 hover:bg-red-300 text-red-900 border-red-300" :
                cat?.tone === "green"  ? "bg-green-200 hover:bg-green-300 text-green-900 border-green-300" :
                                          "bg-yellow-200 hover:bg-yellow-300 text-yellow-900 border-yellow-300"
              )}
              data-testid="beta-feedback-submit"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Camera className="w-3.5 h-3.5 mr-1" />}
              Send feedback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
